// src/services/KbArticleService.js
import { repo } from "../dbservice/repo";
import { KbArticleCategoryModel } from "../models/KbArticleCategoryModel";

class KbArticleService {
  constructor() {
    this.collectionName = "kbArticleCategories";
  }

  // ── Current user ────────────────────────────────────────────────────────────
  get currentUser() {
    if (typeof window === "undefined") return null;
    try {
      const data = localStorage.getItem("userData");
      return data ? JSON.parse(data) : null;
    } catch (e) {
      console.error("[KbArticleService] Failed to parse userData", e);
      return null;
    }
  }

  get companyId() {
    if (typeof window === "undefined") return null;
    try {
      const raw = localStorage.getItem("companyData");
      return raw ? JSON.parse(raw)?.id || null : null;
    } catch {
      return null;
    }
  }

  // ══════════════════════════════════════════════════════════════════════════════
  // ── CATEGORY CRUD ─────────────────────────────────────────────────────────────
  // ══════════════════════════════════════════════════════════════════════════════

  async create(data = {}, currentUser = {}) {
    const user = currentUser?.email ? currentUser : this.currentUser;
    const targetId = data.id || crypto.randomUUID();

    const payload = {
      ...data,
      id: targetId,
      companyId: data.companyId || user?.companyId || this.companyId || null,
      metadata: {
        ...(data.metadata || {}),
        createdBy: user?.email || "system",
        updatedBy: user?.email || "system",
      },
    };

    return await this.upsertData(targetId, payload, { isCreation: true });
  }

  async fetchCategories({ companyId = "", audience = "" } = {}) {
    const cid = companyId || this.companyId;
    const filters = [];
    if (cid)      filters.push({ field: "companyId", value: String(cid) });
    if (audience) filters.push({ field: "audience",  value: audience });

    try {
      return (await repo.fetchData(this.collectionName, filters)) || [];
    } catch (error) {
      console.error("[KbArticleService] Failed to fetch categories:", error);
      return [];
    }
  }

  async fetchCategoryById(id) {
    if (!id) {
      console.error("[KbArticleService] fetchCategoryById: ID missing");
      return null;
    }
    try {
      return (await repo.fetchById(this.collectionName, id)) || null;
    } catch (error) {
      console.error(`[KbArticleService] Failed to fetch category ${id}:`, error);
      return null;
    }
  }

  async update(id, data = {}, currentUser = {}) {
    if (!id) throw new Error("Article category ID is required for update.");
    const user = currentUser?.email ? currentUser : this.currentUser;

    const payload = {
      ...data,
      id,
      metadata: {
        ...(data.metadata || {}),
        updatedBy: user?.email || "system",
      },
    };

    return await this.upsertData(id, payload, { isCreation: false });
  }

  async delete(id) {
    if (!id) throw new Error("Article category ID is required for deletion.");
    try {
      await repo.deleteData(this.collectionName, id);
      return { success: true };
    } catch (error) {
      console.error(`[KbArticleService] Failed to delete category ${id}:`, error);
      throw error;
    }
  }

  // ══════════════════════════════════════════════════════════════════════════════
  // ── ARTICLE ITEM CRUD (nested in `articles[]`) ───────────────────────────────
  // ══════════════════════════════════════════════════════════════════════════════

  async fetchArticles(categoryId) {
    const cat = await this.fetchCategoryById(categoryId);
    if (!cat) return [];
    const source = cat.details || cat;
    return Array.isArray(source.articles) ? source.articles : [];
  }

  async addArticle(categoryId, articleData = {}, currentUser = {}) {
    if (!categoryId) throw new Error("categoryId is required to add an article.");

    const { ok, errors } = KbArticleCategoryModel.validateItem(articleData, {
      isCreation: true,
    });
    if (!ok) throw new Error(`Invalid article: ${errors.join("; ")}`);

    const cat = await this.fetchCategoryById(categoryId);
    if (!cat) throw new Error(`Article category "${categoryId}" not found.`);

    const source   = cat.details || cat;
    const existing = Array.isArray(source.articles) ? source.articles : [];

    const newArticle = {
      id:        `art_${Date.now()}_${Math.random().toString(36).substring(2, 9)}`,
      title:     (articleData.title   || "").trim(),
      content:   (articleData.content || "").trim(),
      tags:      Array.isArray(articleData.tags) ? articleData.tags : [],
      createdAt: new Date().toISOString(),
    };

    await this.update(
      categoryId,
      {
        ...source,
        id: categoryId,
        name: source.name,
        audience: source.audience || "general",
        companyId: source.companyId || this.companyId || "global",
        articles: [...existing, newArticle],
      },
      currentUser,
    );

    return newArticle;
  }

  async updateArticle(categoryId, articleId, updateData = {}, currentUser = {}) {
    if (!categoryId) throw new Error("categoryId is required to update an article.");
    if (!articleId)  throw new Error("articleId is required to update an article.");

    const cat = await this.fetchCategoryById(categoryId);
    if (!cat) throw new Error(`Article category "${categoryId}" not found.`);

    const source   = cat.details || cat;
    const existing = Array.isArray(source.articles) ? [...source.articles] : [];
    const idx      = existing.findIndex((a) => a.id === articleId);
    if (idx === -1) throw new Error(`Article "${articleId}" not found.`);

    const merged = {
      ...existing[idx],
      ...updateData,
      updatedAt: new Date().toISOString(),
    };

    const { ok, errors } = KbArticleCategoryModel.validateItem(merged, {
      isCreation: false,
    });
    if (!ok) throw new Error(`Invalid article: ${errors.join("; ")}`);

    existing[idx] = merged;

    await this.update(
      categoryId,
      { ...source, id: categoryId, articles: existing },
      currentUser,
    );

    return merged;
  }

  async deleteArticle(categoryId, articleId, currentUser = {}) {
    if (!categoryId) throw new Error("categoryId is required to delete an article.");
    if (!articleId)  throw new Error("articleId is required to delete an article.");

    const cat = await this.fetchCategoryById(categoryId);
    if (!cat) throw new Error(`Article category "${categoryId}" not found.`);

    const source   = cat.details || cat;
    const existing = Array.isArray(source.articles) ? [...source.articles] : [];
    const next     = existing.filter((a) => a.id !== articleId);
    if (next.length === existing.length)
      throw new Error(`Article "${articleId}" not found.`);

    await this.update(
      categoryId,
      { ...source, id: categoryId, articles: next },
      currentUser,
    );

    return { success: true };
  }

  // ══════════════════════════════════════════════════════════════════════════════
  // ── CORE PERSISTENCE ORCHESTRATOR ────────────────────────────────────────────
  // ══════════════════════════════════════════════════════════════════════════════

  async upsertData(targetId, data, { isCreation = false } = {}) {
    if (!targetId) throw new Error("Target article category ID is required");

    let finalData = { ...data };

    if (!isCreation) {
      const existing = await this.fetchCategoryById(targetId);
      if (existing) {
        const existingSrc = existing.details || existing;
        finalData = {
          ...existingSrc,
          ...data,
          metadata: {
            ...(existingSrc.metadata || existing.metadata || {}),
            ...(data.metadata || {}),
            createdAt:
              existingSrc.metadata?.createdAt ||
              existingSrc.createdAt ||
              new Date().toISOString(),
            createdBy:
              existingSrc.metadata?.createdBy ||
              existingSrc.createdBy ||
              "system",
          },
        };
      }
    }

    const instance = new KbArticleCategoryModel(
      { ...finalData, id: targetId },
      { isCreation },
    );

    if (!instance.isValid()) {
      throw new Error(
        "Invalid article category payload: id, name, companyId and a valid articles[] array are required.",
      );
    }

    const payload = instance.toPayload();
    const nowIso  = new Date().toISOString();

    payload.metadata = {
      ...(payload.metadata || {}),
      updatedAt: nowIso,
      updatedBy:
        data.metadata?.updatedBy || payload.metadata?.updatedBy || "system",
    };

    return await repo.upsertData(this.collectionName, targetId, payload);
  }
}

export const kbArticleService = new KbArticleService();